/**
 * Trust Hub — verified-only edition.
 *
 * YMYL policy: this page may only contain claims that can be evidenced with a
 * document we actually hold (attorney licence numbers, DBD registration) or a
 * government source that anyone can check. Awards, press mentions, review
 * counts and average ratings were removed because no verifiable source exists
 * in the project. Re-add them only together with a public, checkable citation.
 */
import { credentials as ATTORNEY_CREDS } from "@/data/credentials";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, trustCredentialsSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LeadFormModal } from "@/components/LeadFormModal";
import { BRAND_NAME, BRAND_LEGAL, BRAND_TAX_ID } from "@/lib/brand";
import { BadgeCheck, Scale, FileCheck2, Landmark, ShieldCheck, ExternalLink } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Trust Hub — ใบอนุญาตทนายผู้ทำคำรับรอง 6 ท่าน · หลักฐานตรวจสอบได้";
const DESC =
  "หลักฐานความน่าเชื่อถือที่ตรวจสอบได้ของ NYC Legal & Notary — เลขใบอนุญาตทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร 6 ท่าน เลขนิติบุคคล DBD และหน่วยงานที่รับรองเอกสาร ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี";

export const Route = createFileRoute("/trust")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/trust", "th") },
      { name: "description", content: ctaDesc(DESC, "/trust", "th") },
      {
        name: "keywords",
        content:
          "ใบอนุญาตทนาย Notary, ตรวจสอบทนายผู้ทำคำรับรอง, NYC น่าเชื่อถือ, DBD 0435567000061, สภาทนายความ Notarial Services",
      },
      { property: "og:title", content: ctaTitle(TITLE, "/trust", "th") },
      { property: "og:description", content: ctaDesc(DESC, "/trust", "th") },
      { property: "og:url", content: `${SITE}/trust` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/trust` }, ...hreflangLinks("/trust")],
    scripts: [
      speakableSchema(),
      trustCredentialsSchema({
        pageUrl: `${SITE}/trust`,
        attorneys: ATTORNEY_CREDS.map((c) => {
          const license = c.badge.replace(/^License\s+/i, "").trim();
          const nameEn = c.subtitle.split("—")[0].replace(/^(Mr\.|Miss|Mrs\.)\s*/, "").trim();
          return { name: c.title, nameEn, license };
        }),
      }),
      consolidateGraph([
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
              { "@type": "ListItem", position: 2, name: "Trust Hub", item: `${SITE}/trust` },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${SITE}#organization`,
            name: BRAND_NAME,
            legalName: BRAND_LEGAL,
            taxID: BRAND_TAX_ID,
            url: SITE,
            logo: `${SITE}/og-image.png`,
          }),
        },
      ]),
    ],
  }),
  component: TrustPage,
});

/** หน่วยงานที่เกี่ยวข้องกับสายงานรับรองเอกสาร — ลิงก์ราชการตรวจสอบได้ */
const AUTHORITIES: { name: string; sub: string; url: string }[] = [
  {
    name: "สภาทนายความในพระบรมราชูปถัมภ์",
    sub: "ผู้ออกใบอนุญาตทนายความและขึ้นทะเบียนผู้ทำคำรับรองลายมือชื่อและเอกสาร",
    url: "https://www.lawyerscouncil.or.th/",
  },
  {
    name: "กรมการกงสุล กระทรวงการต่างประเทศ",
    sub: "รับรองนิติกรณ์เอกสาร (Legalization) และเป็นหน่วยงานผู้ออก Apostille เมื่ออนุสัญญามีผลกับไทย 28 ก.พ. 2570",
    url: "https://consular.mfa.go.th/",
  },
  {
    name: "กรมพัฒนาธุรกิจการค้า (DBD)",
    sub: `ตรวจสอบสถานะนิติบุคคล เลขทะเบียน ${BRAND_TAX_ID}`,
    url: "https://datawarehouse.dbd.go.th/",
  },
  {
    name: "NAATI (ออสเตรเลีย)",
    sub: "ตรวจสอบสถานะนักแปลรับรองด้วยหมายเลขผู้ประกอบวิชาชีพบนเว็บไซต์ทางการ",
    url: "https://www.naati.com.au/",
  },
];

const HOW_TO_VERIFY: { icon: typeof Scale; title: string; body: string }[] = [
  {
    icon: Scale,
    title: "ตรวจเลขใบอนุญาตทนายความ",
    body: "เลขใบอนุญาตของทนายผู้ทำคำรับรองแต่ละท่านแสดงอยู่ด้านล่าง สามารถสอบถามสถานะกับสภาทนายความในพระบรมราชูปถัมภ์ได้โดยตรง",
  },
  {
    icon: Landmark,
    title: "ตรวจสถานะนิติบุคคล",
    body: `ค้นเลขทะเบียน ${BRAND_TAX_ID} ในคลังข้อมูลธุรกิจของกรมพัฒนาธุรกิจการค้า เพื่อดูสถานะและที่ตั้งสำนักงานตามที่จดทะเบียน`,
  },
  {
    icon: FileCheck2,
    title: "ตรวจเอกสารที่ได้รับ",
    body: "เอกสารที่ผ่านการรับรองจะมีตราประทับและลายมือชื่อของทนายผู้ทำคำรับรอง พร้อมเลขใบอนุญาตกำกับบนหน้าคำรับรองทุกฉบับ",
  },
  {
    icon: ShieldCheck,
    title: "ตรวจปลายทางก่อนยื่น",
    body: "หน่วยงานปลายทางแต่ละแห่งมีข้อกำหนดต่างกัน แนะนำให้ส่งภาพเอกสารมาให้ตรวจเงื่อนไขก่อน เพื่อลดความเสี่ยงถูกตีกลับ",
  },
];

function TrustPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fafaf7] to-white">
      {/* Hero */}
      <section className="border-b border-amber-500/20 bg-navy-deep text-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <nav className="mb-4 text-xs text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-300">
              หน้าแรก
            </Link>
            <span className="mx-2">›</span>
            <span className="text-amber-300">Trust Hub</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" /> หลักฐานที่ตรวจสอบได้
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            หลักฐานความน่าเชื่อถือ
            <br />
            <span className="text-amber-400">ที่คุณตรวจสอบเองได้ทุกข้อ</span>
          </h1>

          <p
            className="direct-answer aeo-speakable mt-5 max-w-3xl text-base leading-relaxed text-white/85 sm:text-lg"
            data-speakable
          >
            {BRAND_NAME} ดำเนินงานในนาม {BRAND_LEGAL} เลขทะเบียนนิติบุคคล {BRAND_TAX_ID} งานรับรองลายมือชื่อและเอกสาร
            ดำเนินการโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) จำนวน 6 ท่าน
            ซึ่งขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ เลขใบอนุญาตของแต่ละท่านแสดงไว้ในหน้านี้ทั้งหมด
            เพื่อให้ตรวจสอบกับหน่วยงานต้นทางได้โดยตรง (ข้อมูล ณ สิงหาคม 2569)
          </p>

          <div className="mt-7">
            <LeadFormModal
              defaultService="ตรวจเงื่อนไขเอกสารก่อนยื่น"
              trigger={
                <button
                  type="button"
                  className="rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep transition hover:bg-amber-400"
                >
                  ส่งเอกสารให้ตรวจเงื่อนไข ฟรี
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* Attorneys — real licence numbers */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">
          ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร 6 ท่าน
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-navy/70 sm:text-base">
          แต่ละท่านมีใบอนุญาตทนายความและผ่านการอบรมหลักสูตรผู้ทำคำรับรองลายมือชื่อและเอกสารของสภาทนายความฯ
          เลขใบอนุญาตด้านล่างคือเลขเดียวกับที่ปรากฏบนหน้าคำรับรองของเอกสารที่คุณได้รับ
        </p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ATTORNEY_CREDS.map((c) => (
            <li
              key={c.badge}
              className="rounded-2xl border border-navy/10 bg-white p-5 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <Scale className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <div>
                  <h3 className="text-base font-bold text-navy-deep">{c.title}</h3>
                  <p className="mt-0.5 text-xs text-navy/60">{c.subtitle}</p>
                  <p className="mt-2 inline-flex rounded-md bg-amber-50 px-2 py-1 text-xs font-bold text-amber-800">
                    ใบอนุญาตเลขที่ {c.badge.replace(/^License\s+/i, "")}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* How to verify */}
      <section className="border-y border-navy/10 bg-white/70">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16">
          <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">ตรวจสอบเราได้อย่างไร</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {HOW_TO_VERIFY.map((s) => (
              <div key={s.title} className="rounded-2xl border border-navy/10 bg-white p-5">
                <s.icon className="h-5 w-5 text-amber-600" aria-hidden="true" />
                <h3 className="mt-3 text-base font-bold text-navy-deep">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-navy/70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authorities */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">หน่วยงานอ้างอิง</h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-navy/70">
          ทุกขั้นตอนของงานรับรองเอกสารอ้างอิงกฎเกณฑ์ของหน่วยงานด้านล่าง — เปิดเว็บไซต์ทางการเพื่อตรวจสอบเงื่อนไขล่าสุดได้เสมอ
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {AUTHORITIES.map((a) => (
            <li key={a.name} className="rounded-2xl border border-navy/10 bg-white p-5">
              <a
                href={a.url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 text-base font-bold text-navy-deep underline decoration-amber-500/50 underline-offset-4 hover:text-amber-700"
              >
                {a.name}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <p className="mt-1.5 text-sm leading-relaxed text-navy/70">{a.sub}</p>
            </li>
          ))}
        </ul>

        <p className="mt-8 rounded-2xl border border-navy/10 bg-navy/5 p-4 text-xs leading-relaxed text-navy/70">
          หมายเหตุ: ข้อมูลในหน้านี้เป็นข้อมูลทั่วไปเพื่อประกอบการตัดสินใจ ไม่ใช่คำแนะนำทางกฎหมายเฉพาะราย
          เงื่อนไขของหน่วยงานปลายทางอาจเปลี่ยนแปลงได้ กรุณาตรวจสอบกับหน่วยงานนั้นก่อนยื่นทุกครั้ง
        </p>
      </section>

      <SiteCoreLinks />
    </main>
  );
}
